/**
 * Typed access to the `VITE_*` build-time environment.
 *
 * Vite inlines these at build time, so anything here ships to the browser —
 * only ever put publishable values in `VITE_*` variables. The Supabase
 * anon/publishable key is designed for this; the service-role key is not.
 */

function read(key: keyof ImportMetaEnv): string | undefined {
  const value = import.meta.env[key];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

const supabaseUrl = read("VITE_SUPABASE_URL");
const supabaseAnonKey = read("VITE_SUPABASE_ANON_KEY");

export const env = {
  supabaseUrl,
  supabaseAnonKey,
  /** ISO 4217 code every amount in the app is formatted with. */
  currency: read("VITE_CURRENCY") ?? "AED",
  /** True only when both Supabase values are present. */
  hasSupabase: Boolean(supabaseUrl && supabaseAnonKey),
} as const;

/**
 * Narrow the Supabase config to non-optional strings, or explain what's
 * missing. Call this from code paths that genuinely require a backend.
 */
export function requireSupabaseEnv(): { url: string; anonKey: string } {
  if (!supabaseUrl || !supabaseAnonKey) {
    const missing = [
      !supabaseUrl && "VITE_SUPABASE_URL",
      !supabaseAnonKey && "VITE_SUPABASE_ANON_KEY",
    ].filter(Boolean);

    throw new Error(
      `Supabase is not configured. Missing ${missing.join(" and ")}. ` +
        "Copy .env.example to .env.local and fill in the values from " +
        "your project's API settings.",
    );
  }

  return { url: supabaseUrl, anonKey: supabaseAnonKey };
}
